import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useNavigate } from "react-router-dom";

interface ExceptionsCardProps {
  exceptionCount: number;
  criticalExceptionCount: number;
  highExceptionCount: number;
  mediumExceptionCount: number;
  lowExceptionCount: number;
  criticalExceptionPercentage: number;
  highExceptionPercentage: number;
}

export const ExceptionsCard: React.FC<ExceptionsCardProps> = ({
  exceptionCount,
  criticalExceptionCount,
  highExceptionCount,
  mediumExceptionCount,
  lowExceptionCount,
  criticalExceptionPercentage,
  highExceptionPercentage,
}) => {
  const navigate = useNavigate();

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-muted/50 pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Exceptions
        </CardTitle>
        <CardDescription>
          Issues requiring attention this period
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-3xl font-bold">
              {exceptionCount}
            </span>
            <span className="text-sm text-muted-foreground">
              Total exceptions
            </span>
          </div>
          <Button onClick={() => navigate("/app/exceptions")} size="sm">
            View All
          </Button>
        </div>
        <div className="mt-4 space-y-3">
          {/* Critical Exceptions */}
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                Critical
              </span>
              <span className="font-medium">
                {criticalExceptionCount}
              </span>
            </div>
            <Progress
              value={criticalExceptionPercentage}
              className="h-2 bg-muted"
            />
          </div>

          {/* High Exceptions */}
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                High
              </span>
              <span className="font-medium">
                {highExceptionCount}
              </span>
            </div>
            <Progress
              value={highExceptionPercentage}
              className="h-2 bg-muted"
            />
          </div>

          {/* Medium Exceptions */}
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                Medium
              </span>
              <span className="font-medium">
                {mediumExceptionCount}
              </span>
            </div>
            <Progress
              value={exceptionCount > 0 ? (mediumExceptionCount / exceptionCount) * 100 : 0}
              className="h-2 bg-muted"
            />
          </div>

          {/* Low Exceptions */}
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                Low
              </span>
              <span className="font-medium">
                {lowExceptionCount}
              </span>
            </div>
            <Progress
              value={exceptionCount > 0 ? (lowExceptionCount / exceptionCount) * 100 : 0}
              className="h-2 bg-muted"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 